import { FiltersType, ListingRequest } from '@/api/types';
import { client } from '@/client';
import { Account, ListAccountsResponse } from '@/client/accounts';
import {
  Course,
  ListCourseAccountResponse,
  MakeAvailableAccountsRequest,
  MakeAvailableCoursesToAccountRequest,
} from '@/client/courses';
import { MessageResponseModel } from '@/client/models';
import {
  AddAvailableAccountsMutation,
  AddAvailableCoursesToAccountMutation,
  GenericForbiddenApiError,
} from '@/hooks/query';
import { COURSES_QUERY_KEY } from '@/hooks/query/courses.query-keys';
import { partialRequests } from '@/utils/helpers';
import {
  useMutation,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';

export const useMakeAccountsAvailable = (): {
  add: (payload: MakeAvailableAccountsRequest) => Promise<MessageResponseModel>;
} & AddAvailableAccountsMutation => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    MakeAvailableAccountsRequest
  >('addAvailableAccounts', (payload: MakeAvailableAccountsRequest) =>
    client.courses.saveAvailableAccounts({
      courseId: payload.courseId,
      accounts: payload.accounts,
    }),
  );

  return { add: mutateAsync, ...(rest as any) };
};

export const useMakeCoursesAvailableToAccount = (): {
  add: (
    payload: MakeAvailableCoursesToAccountRequest,
  ) => Promise<MessageResponseModel>;
} & AddAvailableCoursesToAccountMutation => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    MakeAvailableCoursesToAccountRequest
  >('addAvailableAccounts', (payload: MakeAvailableCoursesToAccountRequest) =>
    client.courses.saveAvailableCoursesToAccount({
      courses: payload.courses,
      accountId: payload.accountId,
    }),
  );

  return { add: mutateAsync, ...(rest as any) };
};

export const useMakeAccountsUnavailable = (): {
  remove: (
    payload: MakeAvailableAccountsRequest,
  ) => Promise<MessageResponseModel>;
} & AddAvailableAccountsMutation => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    MakeAvailableAccountsRequest
  >('removeAvailableAccounts', (payload: MakeAvailableAccountsRequest) =>
    client.courses.saveUnavailableAccounts({
      courseId: payload.courseId,
      accounts: payload.accounts,
    }),
  );

  return { remove: mutateAsync, ...(rest as any) };
};

export const useUnavailableAccounts = (
  params: UseQueryOptions & {
    token?: string;
    take?: number;
    skip?: number;
    sort?: string[];
    filters?: FiltersType;
  } = {},
  courseId?: string,
): UseQueryResult<ListAccountsResponse> & {
  accounts?: ListAccountsResponse;
} => {
  const {
    take = 5,
    skip = 0,
    sort,
    filters,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;
  const { data, ...rest } = useQuery(
    [
      `${COURSES_QUERY_KEY}Accounts`,
      take,
      skip,
      JSON.stringify(sort),
      JSON.stringify(filters),
    ],
    () => client.courses.getUnavailableAccounts(params, courseId),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    accounts: data,
    ...(rest as UseQueryResult<ListAccountsResponse>),
  };
};

export const useUnavailableAccountsPartialRequest = (
  params: UseQueryOptions & {
    sort?: string[];
    filters?: FiltersType;
  },
  courseId?: string,
): UseQueryResult<Account[]> & {
  accounts: Account[] | undefined;
} => {
  const {
    sort,
    filters,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;
  const unavailableAccountsRequest = async (params: ListingRequest) =>
    await client.courses.getUnavailableAccounts({ ...params, sort }, courseId);
  const { data, ...rest } = useQuery(
    [`${COURSES_QUERY_KEY}UnavailableAccountsPartial`],
    () => partialRequests(filters || [], unavailableAccountsRequest),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    accounts: data,
    ...(rest as UseQueryResult<Account[]>),
  };
};

export const useCourseAccounts = (
  params: UseQueryOptions & {
    take?: number;
    skip?: number;
    sort?: string[];
    filters?: FiltersType;
  },
  courseId?: string,
): UseQueryResult<ListCourseAccountResponse> & {
  courseAccounts: ListCourseAccountResponse | undefined;
} => {
  const {
    take = 5,
    skip = 0,
    sort,
    filters,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [
      `${COURSES_QUERY_KEY}CourseAccounts`,
      take,
      skip,
      JSON.stringify(sort),
      JSON.stringify(filters),
    ],
    () => client.courses.getCourseAccounts(params, courseId),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    courseAccounts: data,
    ...(rest as UseQueryResult<ListCourseAccountResponse>),
  };
};

export const useUnavailableCoursesToAccount = (
  params: UseQueryOptions & {
    sort?: string[];
    filters?: FiltersType;
  } & { accountId: string },
): UseQueryResult<Course[]> & {
  courses: Course[] | undefined;
} => {
  const {
    accountId,
    sort,
    filters,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;
  const unavailableCoursesRequest = async (params: ListingRequest) =>
    await client.courses.getUnavailableCoursesToAccount(
      { ...params, sort },
      accountId,
    );
  const { data, ...rest } = useQuery(
    [`${COURSES_QUERY_KEY}-partial`],
    () => partialRequests(filters || [], unavailableCoursesRequest),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    courses: data,
    ...(rest as UseQueryResult<Course[]>),
  };
};
